/**
 * DASHBOARD SIDEBAR COMPONENT
 * ===========================
 * 
 * The main navigation sidebar for authenticated users. Provides quick access
 * to all major sections of the application with visual indicators for the
 * current page.
 * 
 * FEATURES:
 * - Collapsible design for space efficiency
 * - Current page highlighting
 * - Organized sections (Overview, Decisions, Tools)
 * - Quick action buttons
 * - Custom brand logo integration
 * - Responsive behavior
 */

import { 
  BarChart3, 
  Calendar, 
  Home, 
  List, 
  Plus, 
  Settings, 
  HelpCircle,
  Activity,
  BookOpen,
  GraduationCap
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
} from "./ui/sidebar";
import { Logo, LogoIcon } from "./Logo";
import { useSidebar } from "./ui/sidebar";
import type { Page } from "../src/types";

interface DashboardSidebarProps {
  onLogoClick: () => void;                          // Logo click handler
  onNavigateHome: () => void;                       // Dashboard/home navigation
  onNewDecision: () => void;                        // New decision action
  onNavigateToMyDecisions: () => void;              // My decisions page
  onNavigateToAnalytics: () => void;                // Analytics page
  onNavigateToReviews: () => void;                  // Reviews page
  onNavigateToLog: () => void;                      // Activity log page
  onNavigateToSettings: () => void;                 // Settings page
  onNavigateToHelp: () => void;                     // Help page
  onNavigateToAppBlog: () => void;                  // In-app blog page
  onNavigateToAppBestPractices: () => void;         // In-app best practices page
  currentPage: Page;                                // Current active page
}

export function DashboardSidebar({
  onLogoClick,
  onNavigateHome,
  onNewDecision,
  onNavigateToMyDecisions,
  onNavigateToAnalytics,
  onNavigateToReviews,
  onNavigateToLog,
  onNavigateToSettings,
  onNavigateToHelp,
  onNavigateToAppBlog,
  onNavigateToAppBestPractices,
  currentPage,
}: DashboardSidebarProps) {
  const { state } = useSidebar();

  /**
   * Navigation menu items organized by category
   */
  
  // Main navigation items (Overview section)
  const overviewItems = [
    {
      title: "Dashboard",
      icon: Home,
      onClick: onNavigateHome,
      isActive: currentPage === "dashboard",
      description: "Overview and recent activity"
    },
  ];

  // Decision-related navigation items
  const decisionItems = [
    {
      title: "New Decision",
      icon: Plus,
      onClick: onNewDecision,
      isActive: currentPage === "new-decision",
      description: "Record a new decision"
    },
    {
      title: "My Decisions",
      icon: List,
      onClick: onNavigateToMyDecisions,
      isActive: currentPage === "my-decisions" || currentPage === "decision-detail",
      description: "View and manage decisions"
    },
    {
      title: "Reviews",
      icon: Calendar,
      onClick: onNavigateToReviews,
      isActive: currentPage === "reviews",
      description: "Scheduled decision reviews"
    },
  ];

  // Tools and analysis items
  const toolsItems = [
    {
      title: "Analytics",
      icon: BarChart3,
      onClick: onNavigateToAnalytics,
      isActive: currentPage === "analytics",
      description: "Insights and trends"
    },
    {
      title: "Activity Log",
      icon: Activity,
      onClick: onNavigateToLog,
      isActive: currentPage === "log",
      description: "Track your activity"
    },
  ];

  // Resources items
  const resourcesItems = [
    {
      title: "Blog",
      icon: BookOpen,
      onClick: onNavigateToAppBlog,
      isActive: currentPage === "app-blog",
      description: "Decision-making insights and articles"
    },
    {
      title: "Best Practices",
      icon: GraduationCap,
      onClick: onNavigateToAppBestPractices,
      isActive: currentPage === "app-best-practices",
      description: "Expert guidance and strategies"
    },
  ];

  // Settings and support items
  const accountItems = [
    {
      title: "Settings",
      icon: Settings,
      onClick: onNavigateToSettings,
      isActive: currentPage === "settings",
      description: "App preferences"
    },
    {
      title: "Help",
      icon: HelpCircle,
      onClick: onNavigateToHelp,
      isActive: currentPage === "help",
      description: "Documentation and support"
    },
  ];

  return (
    <Sidebar className="border-r z-50" collapsible="icon" variant="sidebar">
      {/* SIDEBAR HEADER - Logo and branding */}
      <SidebarHeader className="border-b p-4">
        {state === "collapsed" ? (
          <LogoIcon 
            size="sm" 
            onClick={onLogoClick}
            className="mx-auto"
          />
        ) : (
          <Logo 
            size="md" 
            onClick={onLogoClick}
            showText={true}
          />
        )}
      </SidebarHeader>

      {/* SIDEBAR CONTENT - Navigation menu */}
      <SidebarContent className="p-4">
        
        {/* OVERVIEW SECTION */}
        <SidebarGroup>
          <SidebarGroupLabel>Overview</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {overviewItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    onClick={item.onClick}
                    isActive={item.isActive}
                    tooltip={item.description}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* DECISIONS SECTION */}
        <SidebarGroup>
          <SidebarGroupLabel>Decisions</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {decisionItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    onClick={item.onClick}
                    isActive={item.isActive}
                    tooltip={item.description}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* TOOLS SECTION */}
        <SidebarGroup>
          <SidebarGroupLabel>Tools</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {toolsItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    onClick={item.onClick}
                    isActive={item.isActive}
                    tooltip={item.description}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* RESOURCES SECTION */}
        <SidebarGroup>
          <SidebarGroupLabel>Resources</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {resourcesItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    onClick={item.onClick}
                    isActive={item.isActive}
                    tooltip={item.description}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* ACCOUNT SECTION */}
        <SidebarGroup>
          <SidebarGroupLabel>Account</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {accountItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    onClick={item.onClick}
                    isActive={item.isActive}
                    tooltip={item.description}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      {/* SIDEBAR FOOTER - Additional info or actions */}
      <SidebarFooter className="border-t p-4">
        <div className="text-xs text-muted-foreground space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground/60">Quick access:</span>
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
              <span className="text-xs">⌘</span>K
            </kbd>
          </div>
          <p>Version 1.0.0</p>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}